/**
 * Random Outcomes drawer: fit the open height to the rendered content.
 *
 * The drawer used to open to a fixed 80% of the screen, which left a big empty
 * gap above the Yes / No buttons on most phones. It now opens to exactly the
 * height its content needs (handle + lock row + title + full disclaimer text +
 * Yes/No footer incl. safe-area inset), clamped between a floor and a cap:
 *
 *  - cap   : 90% of the available height. Past the cap the text area scrolls
 *            inside the drawer and the buttons stay pinned (large text sizes,
 *            landscape).
 *  - floor : never shorter than the Explore resting position (+ a little), so
 *            Accept always has a visible slide-down to Explore's resting top and
 *            the hand-off to Explore's own drawer is covered on tall screens.
 *
 * Pure functions (no React). Measuring the DOM is in measureDrawerContentPx;
 * the component calls both on mount and on every resize / text-size change.
 */
import { EXPLORE_REST_TOP_FRACTION } from './loginCasinoReveal';

/** Open height never exceeds this share of the available height. */
export const DRAWER_FIT_MAX_FRACTION = 0.9;
/** ...and never drops below Explore's resting top, plus a little travel for the slide-down. */
export const DRAWER_FIT_MIN_FRACTION = EXPLORE_REST_TOP_FRACTION + 0.05;
/** Absolute floor in px (very small windows). */
export const DRAWER_FIT_MIN_PX = 220;
/** Added to the measured content so sub-pixel rounding never produces a scrollbar. */
export const DRAWER_FIT_SLACK_PX = 1;

export type DrawerFitInput = {
  /** Natural content height in px (everything inside the drawer, padding included). */
  contentPx: number;
  /** Height in px of the area the drawer lives in (the fixed, full-screen root). */
  viewportPx: number;
  maxFraction?: number;
  minFraction?: number;
  minPx?: number;
};

/**
 * Open height in px: the content height clamped to [floor, cap].
 * Returns null when it cannot be computed (not laid out yet), so the caller
 * keeps the CSS fallback instead of collapsing the drawer.
 */
export function computeDrawerOpenHeight(input: DrawerFitInput): number | null {
  const { contentPx, viewportPx } = input;
  if (!Number.isFinite(viewportPx) || viewportPx <= 0) return null;
  if (!Number.isFinite(contentPx) || contentPx <= 0) return null;

  const maxFraction = input.maxFraction ?? DRAWER_FIT_MAX_FRACTION;
  const minFraction = input.minFraction ?? DRAWER_FIT_MIN_FRACTION;
  const minPx = input.minPx ?? DRAWER_FIT_MIN_PX;

  const cap = Math.floor(viewportPx * maxFraction);
  // The cap always wins over the floor (tiny windows).
  const floor = Math.min(cap, Math.max(minPx, Math.ceil(viewportPx * minFraction)));
  const wanted = Math.ceil(contentPx) + DRAWER_FIT_SLACK_PX;
  return Math.min(cap, Math.max(floor, wanted));
}

/** True when the content needs more than the cap allows (text area scrolls). */
export function isDrawerHeightCapped(input: DrawerFitInput): boolean {
  const open = computeDrawerOpenHeight(input);
  if (open == null) return false;
  const cap = Math.floor(input.viewportPx * (input.maxFraction ?? DRAWER_FIT_MAX_FRACTION));
  return Math.ceil(input.contentPx) + DRAWER_FIT_SLACK_PX > cap;
}

/**
 * How far (px) the drawer slides down on Accept so its top lands on Explore's
 * resting top. The drawer is bottom-anchored, so open top = viewport - open height;
 * shift = restTop - openTop. Never negative.
 */
export function computeDrawerLowerShiftPx(
  openHeightPx: number,
  viewportPx: number,
  restTopFraction: number = EXPLORE_REST_TOP_FRACTION
): number {
  if (!Number.isFinite(openHeightPx) || !Number.isFinite(viewportPx) || viewportPx <= 0) return 0;
  const openTop = viewportPx - openHeightPx;
  const restTop = viewportPx * restTopFraction;
  return Math.max(0, Math.round(restTop - openTop));
}

// ---------------------------------------------------------------------------
// DOM measurement (called at runtime only).
// ---------------------------------------------------------------------------

const SCROLL_CLASS = 'ro-drawer__scroll';
const INNER_CLASS = 'ro-drawer__inner';

const px = (v: string): number => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Natural height of the drawer's content. The scrolling text area is measured
 * through its children (rect + margins), not its own box, so the result does not
 * depend on the height the drawer currently has: it can shrink as well as grow.
 * Fixed rows (handle, title, hint, footer) are measured as rendered, so text
 * size / bold / display-zoom settings and the bottom safe-area inset count.
 */
export function measureDrawerContentPx(drawer: HTMLElement): number {
  const inner = drawer.querySelector<HTMLElement>(`.${INNER_CLASS}`);
  if (!inner) return 0;

  const rectHeight = (el: Element): number => el.getBoundingClientRect().height;
  const withMargins = (el: Element): number => {
    const cs = window.getComputedStyle(el);
    return rectHeight(el) + px(cs.marginTop) + px(cs.marginBottom);
  };

  let total = Math.max(0, rectHeight(drawer) - rectHeight(inner));
  Array.from(inner.children).forEach((child) => {
    if (child.classList.contains(SCROLL_CLASS)) {
      const cs = window.getComputedStyle(child);
      const kids = Array.from(child.children);
      const body = kids.length
        ? kids.reduce((sum, k) => sum + withMargins(k), 0)
        : child.scrollHeight;
      total += body + px(cs.paddingTop) + px(cs.paddingBottom);
    } else {
      total += rectHeight(child);
    }
  });
  return total;
}
